<template>
  <div class="mb-4" v-if="show">
    <div class="">
      <div class="mb-2">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="purchaseAsBusiness" v-model="purchaseAsBusiness" />
          <label class="form-check-label" for="purchaseAsBusiness">
            <small>{{ 'Purchase as business' | translate }}</small>
          </label>
        </div>
      </div>

      <!-- {{-- ===== PERSON: full form only in non-simplified mode ===== --}} -->
      <template v-if="type === 'person' && !simplifiedCheckout">
        <div class="row" v-if="isAuthenticated">
          <div class="col">
            <div class="mb-2">
              <label for="name">
                <small>{{ 'Name' | translate }}</small>
              </label>
              <input
                key="name"
                id="name"
                v-model="name"
                autocomplete="off"
                class="form-control"
                :class="{ 'is-invalid': 'name' in errors }"
                type="text"
              />
            </div>
          </div>
          <div class="col">
            <div class="mb-2">
              <label for="last_name">
                <small>{{ 'Last name' | translate }}</small>
              </label>
              <input
                id="last_name"
                v-model="last_name"
                autocomplete="off"
                class="form-control"
                :class="{ 'is-invalid': 'last_name' in errors }"
                type="text"
              />
            </div>
          </div>
        </div>

        <div class="mb-2" v-if="display_phone_field">
          <label for="phone">
            <small>{{ 'Phone' | translate }}</small>
          </label>
          <input
            v-mask="'###############'"
            key="phone"
            id="phone"
            v-model="phone"
            autocomplete="off"
            class="form-control"
            :class="{ 'is-invalid': 'phone' in errors }"
            type="text"
          />
          <small><InputErrors name="phone"></InputErrors></small>
        </div>

        <div class="mb-2">
          <label for="address1">
            <small>{{ 'Address' | translate }}</small>
          </label>
          <input
            id="address1"
            v-model="address1"
            autocomplete="off"
            class="form-control"
            :class="{ 'is-invalid': 'address1' in errors }"
            type="text"
          />
        </div>

        <div class="mb-2" v-if="student.type === 'company' || student.vat_number">
          <label for="address2">
            <small>{{ 'Address 2' | translate }}</small>
          </label>
          <input
            id="address2"
            v-model="address2"
            autocomplete="off"
            class="form-control"
            :class="{ 'is-invalid': 'address2' in errors }"
            type="text"
          />
        </div>

        <div class="row">
          <div class="col-5 col-lg-4">
            <div class="mb-2">
              <label for="postal_code">
                <small>{{ 'Postal code' | translate }}</small>
              </label>
              <input
                id="postal_code"
                v-model="postal_code"
                autocomplete="off"
                class="form-control"
                :class="{ 'is-invalid': 'postal_code' in errors }"
                type="text"
              />
              <small><InputErrors name="postal_code"></InputErrors></small>
            </div>
          </div>
          <div class="col-7 col-lg-8">
            <div class="mb-2">
              <label for="city">
                <small>{{ 'City' | translate }}</small>
              </label>
              <input
                id="city"
                v-model="city"
                autocomplete="off"
                class="form-control"
                :class="{ 'is-invalid': 'city' in errors }"
                type="text"
              />
            </div>
          </div>
        </div>

        <div class="mb-2">
          <label for="country">
            <small>{{ 'Country' | translate }}</small>
          </label>
          <Select
            inputId="country"
            :class="{ 'is-invalid': 'country' in errors }"
            v-model="country"
            autocomplete="off"
            :reduce="(c) => c.code"
            :options="countries"
            :placeholder="$t('Choose country')"
          ></Select>
        </div>
      </template>

      <!-- {{-- ===== COMPANY: NIP lookup flow (used in both simplified & non-simplified) ===== --}} -->
      <template v-if="type === 'company'">
        <div class="mb-2" v-if="display_phone_field">
          <label for="phone">
            <small>{{ 'Phone' | translate }}</small>
          </label>
          <input
            v-mask="'###############'"
            key="phone"
            id="phone"
            v-model="phone"
            autocomplete="off"
            class="form-control"
            :class="{ 'is-invalid': 'phone' in errors }"
            type="text"
          />
          <small><InputErrors name="phone"></InputErrors></small>
        </div>
        <div class="mb-2">
          <label for="vat_number" class="small">
            {{ 'Vat number / Tax id' | translate }}
          </label>
          <div class="input-group">
            <input
              v-mask="'XXXX#####XXX###'"
              key="vat_number"
              id="vat_number"
              v-model="vat_number"
              autocomplete="off"
              class="form-control"
              :class="{ 'is-invalid': 'vat_number' in errors }"
              type="text"
              :placeholder="countryPlaceholder"
            />
            <button :disabled="!showCheckByNip" class="btn btn-dark px-4 fw-semibold" @click="checkByNip">
              {{ 'Download' | translate }}
            </button>
          </div>
        </div>

        <!-- After successful NIP lookup: populated fields (editable) -->
        <template v-if="nipLookupState === 'found'">
          <div class="mb-2">
            <label for="company_name">
              <small>{{ 'Company name' | translate }}</small>
            </label>
            <input
              id="company_name"
              v-model="company_name"
              autocomplete="off"
              class="form-control"
              :class="{ 'is-invalid': 'company_name' in errors }"
              type="text"
            />
          </div>

          <div class="mb-2">
            <label for="address1">
              <small>{{ 'Address' | translate }}</small>
            </label>
            <input
              id="address1"
              v-model="address1"
              autocomplete="off"
              class="form-control"
              :class="{ 'is-invalid': 'address1' in errors }"
              type="text"
            />
          </div>

          <div class="row">
            <div class="col-5 col-lg-4">
              <div class="mb-2">
                <label for="postal_code">
                  <small>{{ 'Postal code' | translate }}</small>
                </label>
                <input
                  id="postal_code"
                  v-model="postal_code"
                  autocomplete="off"
                  class="form-control"
                  :class="{ 'is-invalid': 'postal_code' in errors }"
                  type="text"
                />
                <small><InputErrors name="postal_code"></InputErrors></small>
              </div>
            </div>
            <div class="col-7 col-lg-8">
              <div class="mb-2">
                <label for="city">
                  <small>{{ 'City' | translate }}</small>
                </label>
                <input
                  id="city"
                  v-model="city"
                  autocomplete="off"
                  class="form-control"
                  :class="{ 'is-invalid': 'city' in errors }"
                  type="text"
                />
              </div>
            </div>
          </div>

          <div class="mb-2">
            <label for="country">
              <small>{{ 'Country' | translate }}</small>
            </label>
            <Select
              inputId="country"
              :class="{ 'is-invalid': 'country' in errors }"
              v-model="country"
              autocomplete="off"
              :reduce="(c) => c.code"
              :options="countries"
              :placeholder="$t('Choose country')"
            ></Select>
          </div>
        </template>

        <!-- {{-- After failed NIP lookup: required fields for manual entry --}} -->
        <template v-if="nipLookupState === 'not_found'">
          <div class="small text-danger mb-2">
            {{ 'Not found. Please enter the information manually.' | translate }}
          </div>

          <div class="mb-2">
            <label for="company_name">
              <small>{{ 'Company name' | translate }}</small>
            </label>
            <input
              id="company_name"
              v-model="company_name"
              autocomplete="off"
              class="form-control"
              :class="{ 'is-invalid': 'company_name' in errors }"
              type="text"
            />
          </div>

          <div class="mb-2">
            <label for="address1">
              <small>{{ 'Address' | translate }}</small>
            </label>
            <input
              id="address1"
              v-model="address1"
              autocomplete="off"
              class="form-control"
              :class="{ 'is-invalid': 'address1' in errors }"
              type="text"
            />
          </div>

          <div class="mb-2">
            <label for="address2">
              <small>{{ 'Address 2' | translate }}</small>
            </label>
            <input
              id="address2"
              v-model="address2"
              autocomplete="off"
              class="form-control"
              :class="{ 'is-invalid': 'address2' in errors }"
              type="text"
            />
          </div>

          <div class="row">
            <div class="col-5 col-lg-4">
              <div class="mb-2">
                <label for="postal_code">
                  <small>{{ 'Postal code' | translate }}</small>
                </label>
                <input
                  id="postal_code"
                  v-model="postal_code"
                  autocomplete="off"
                  class="form-control"
                  :class="{ 'is-invalid': 'postal_code' in errors }"
                  type="text"
                />
                <small><InputErrors name="postal_code"></InputErrors></small>
              </div>
            </div>
            <div class="col-7 col-lg-8">
              <div class="mb-2">
                <label for="city">
                  <small>{{ 'City' | translate }}</small>
                </label>
                <input
                  id="city"
                  v-model="city"
                  autocomplete="off"
                  class="form-control"
                  :class="{ 'is-invalid': 'city' in errors }"
                  type="text"
                />
              </div>
            </div>
          </div>

          <div class="mb-2">
            <label for="country">
              <small>{{ 'Country' | translate }}</small>
            </label>
            <Select
              inputId="country"
              :class="{ 'is-invalid': 'country' in errors }"
              v-model="country"
              autocomplete="off"
              :reduce="(c) => c.code"
              :options="countries"
              :placeholder="$t('Choose country')"
            ></Select>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import Select from 'vue-select'
import InputErrors from './InputErrors'
import { mask } from 'vue-the-mask'
import _ from 'lodash'

const nonEsentialSigns = /[^a-zA-Z0-9]/gi

export default {
  directives: { mask },
  components: {
    Select,
    InputErrors,
  },
  data() {
    return {
      manualInvoiceDataWarning: 0,
      checkedNumbers: [],
      nipLookupState: 'idle', // 'idle' | 'found' | 'not_found'
    }
  },
  created() {
    // If user already has company data, show the editable fields immediately
    if (this.type === 'company' && this.company_name) {
      this.nipLookupState = 'found'
    }
  },
  methods: {
    displayNipNotFoundWarning() {
      this.manualInvoiceDataWarning = 1
      setTimeout(() => {
        this.manualInvoiceDataWarning = 0
      }, 3000)
    },
    checkByNip() {
      if (this.checkedNumbers.length > 9) {
        this.nipLookupState = 'not_found'
        return false
      }

      this.checkedNumbers = [...this.checkedNumbers, this.vat_number]

      axios
        .post('/json/checkorganization', {
          nip: this.vat_number,
        })
        .then(({ data }) => {
          if (data.name) {
            this.company_name = data.name
            this.country = 'PL'
            this.nipLookupState = 'found'
          }
          if (data.city) {
            this.city = data.city
          }
          if (data.zipCode) {
            this.postal_code = data.zipCode
          }
          if (data.street) {
            this.address1 = data.street + ' ' + data.propertyNumber
            if (data.apartmentNumber) {
              this.address1 += '/' + data.apartmentNumber
            }
          }
        })
        .catch((error) => {
          this.nipLookupState = 'not_found'
        })
    },
    tooltipStart(event) {
      bootstrap.Tooltip.getOrCreateInstance(event.target).show()
    },
    tooltipStop(event) {
      bootstrap.Tooltip.getOrCreateInstance(event.target).hide()
    },
    updateCountry(value) {
      this.$store.commit('updateUser', { country: value })
    },
  },
  watch: {
    vat_number() {
      // Reset lookup state whenever the NIP changes
      this.nipLookupState = 'idle'

      if (this.type === 'company' && this.company_name) {
        this.nipLookupState = 'found'
      }
    },
  },
  computed: {
    simplifiedCheckout() {
      if (_.get(this.$store.state, 'course.type') === 'physical') {
        return false
      }

      if (_.get(this.$store.state, 'page.options.simplified_checkout_page')) {
        return true
      }

      return false
    },
    showCheckByNip() {
      if (_.includes(this.checkedNumbers, this.vat_number)) {
        return false
      }
      if (this.vat_number) {
        let nip = this.vat_number.replace(nonEsentialSigns, '')
        if (nip.length === 10 || (nip.length === 12 && nip.toLowerCase().startsWith('pl'))) {
          return true
        }
      }
      return false
    },
    errors() {
      return this.$store.state.errors
    },
    display_phone_field() {
      if (_.get(this.$store.state, 'course.type', false) === 'physical') {
        return true
      }

      if (_.get(this.$store.state, 'page.options.simplified_checkout_page')) {
        return false
      }

      return _.get(this.$store.state, 'page.options.display_phone_field', false)
    },
    type: {
      get() {
        return _.get(this.$store.state, 'user.type', 'person')
      },
      set(type) {
        if (type === 'person') {
          this.$store.commit('updateUser', { type, vat_number: null })
        }
        this.nipLookupState = 'idle'
        this.$store.commit('updateUser', { type })
      },
    },
    purchaseAsBusiness: {
      get() {
        return this.type === 'company'
      },
      set(checked) {
        this.type = checked ? 'company' : 'person'
      },
    },
    name: {
      get() {
        return _.get(this.$store.state, 'user.name')
      },
      set(name) {
        this.$store.commit('updateUser', { name })
      },
    },
    last_name: {
      get() {
        return _.get(this.$store.state, 'user.last_name')
      },
      set(last_name) {
        this.$store.commit('updateUser', { last_name })
      },
    },
    vat_number: {
      get() {
        return _.get(this.$store.state, 'user.vat_number')
      },
      set(vat_number) {
        this.$store.commit('updateUser', { vat_number })
      },
    },
    company_name: {
      get() {
        return _.get(this.$store.state, 'user.company_name')
      },
      set(company_name) {
        this.$store.commit('updateUser', { company_name })
      },
    },
    address1: {
      get() {
        return _.get(this.$store.state, 'user.address1')
      },
      set(address1) {
        this.$store.commit('updateUser', { address1 })
      },
    },
    address2: {
      get() {
        return _.get(this.$store.state, 'user.address2')
      },
      set(address2) {
        this.$store.commit('updateUser', { address2 })
      },
    },
    postal_code: {
      get() {
        return _.get(this.$store.state, 'user.postal_code')
      },
      set(postal_code) {
        this.$store.commit('updateUser', { postal_code })
      },
    },
    city: {
      get() {
        return _.get(this.$store.state, 'user.city')
      },
      set(city) {
        this.$store.commit('updateUser', { city })
      },
    },
    phone: {
      get() {
        return _.get(this.$store.state, 'user.phone')
      },
      set(phone) {
        this.$store.commit('updateUser', { phone })
      },
    },

    countryPlaceholder() {
      if (this.country === 'PL') {
        return '1234567890'
      } else if (_.includes(this.euCountries, this.country)) {
        return 'AB1234567890'
      }

      return this.$t('VAT EU')
    },
    country: {
      get() {
        return _.get(this.$store.state, 'user.country')
      },
      set(country) {
        this.$store.commit('updateUser', { country })
      },
    },
    show() {
      let view = this.$store.state.view
      if (this.student.email) {
        return true
      }
      if (view === 'register') {
        return true
      }
      if (view === 'login') {
        return false
      }
      return false
    },
    countries() {
      return _.toPairs(this.$store.state.countries).map(([key, value]) => ({
        code: key,
        label: value,
      }))
    },
    euCountries() {
      return _.toPairs(this.$store.state.VatRatesEU).map(([key, value]) => key)
    },
    student() {
      return _.get(this.$store.state, 'user')
    },
    isAuthenticated() {
      return _.get(this.$store.state, 'user.email') ? true : false
    },
  },
}
</script>
